import { fetchAPI } from "./utils";
import {
    GetCompanyFileCategoriesParams,
    CreateCompanyFileCategoryParams,
    UpdateCompanyFileCategoryParams,
    DeleteCompanyFileCategoryParams,
} from "../types/requestInputs";
import {
    GetCompanyFileCategoriesResponse,
    CreateCompanyFileCategoryResponse,
    UpdateCompanyFileCategoryResponse,
    DeleteCompanyFileCategoryResponse,
    GetCompanyFilesResponse,
} from "../types/requestsOutputs";

// Get all company file categories
export const getCompanyFileCategories = async ({
    token,
}: GetCompanyFileCategoriesParams): Promise<GetCompanyFileCategoriesResponse> => {
    return fetchAPI<GetCompanyFileCategoriesResponse>("/get-company-file-categories", "GET", undefined, token);
};

// Create a new company file category
export const createCompanyFileCategory = async ({
    token,
    name,
}: CreateCompanyFileCategoryParams): Promise<CreateCompanyFileCategoryResponse> => {
    return fetchAPI<CreateCompanyFileCategoryResponse>("/create-company-file-category", "POST", { name }, token);
};

// Update company file category
export const updateCompanyFileCategory = async ({
    token,
    categoryId,
    newName,
}: UpdateCompanyFileCategoryParams): Promise<UpdateCompanyFileCategoryResponse> => {
    return fetchAPI<UpdateCompanyFileCategoryResponse>(
        "/update-company-file-category",
        "POST",
        { categoryId, newName },
        token,
    );
};

// Delete a company file category
export const deleteCompanyFileCategory = async ({
    token,
    categoryId,
}: DeleteCompanyFileCategoryParams): Promise<DeleteCompanyFileCategoryResponse> => {
    return fetchAPI<DeleteCompanyFileCategoryResponse>("/delete-company-file-category", "POST", { categoryId }, token);
};

// Upload file with metadata
export const uploadFileWithMetadata = async ({
    token,
    fileData,
}: {
    token: string;
    fileData: {
        documentUrl: string;
        documentName: string;
        companyRagFolderId?: string;
        companyFileCategoryIds?: string[];
        companyFileTagIds?: string[];
        title?: string;
        description?: string;
        author?: string;
        date?: string;
        type?: string;
        language?: string;
        rightsOwner?: string;
        creditLine?: string;
        companyUsageTypeMetadataIds?: string[];
        companyRightsTermMetadataIds?: string[];
        companyTerritoryMetadataIds?: string[];
        companyAllowedMediaMetadataIds?: string[];
    };
}): Promise<any> => {
    return fetchAPI<any>("/queue-file-upload", "POST", fileData, token);
};

// Update file metadata
export const updateCompanyFileMetadata = async ({
    token,
    fileId,
    metadata,
}: {
    token: string;
    fileId: string;
    metadata: {
        title?: string | null;
        description?: string | null;
        author?: string | null;
        date?: string | null;
        type?: string | null;
        language?: string | null;
        companyFileCategoryIds?: string[];
        companyRagFolderId?: string | null;
        companyFileTagIds?: string[];
        rightsOwner?: string | null;
        creditLine?: string | null;
        companyUsageTypeMetadataIds?: string[];
        companyRightsTermMetadataIds?: string[];
        companyTerritoryMetadataIds?: string[];
        companyAllowedMediaMetadataIds?: string[];
    };
}): Promise<any> => {
    return fetchAPI<any>(
        "/update-company-file-metadata",
        "POST",
        {
            companyFileId: fileId,
            ...metadata,
        },
        token,
    );
};

// Get company files with pagination, filtering, and sorting
export const getCompanyFiles = async ({
    token,
    queryParams,
}: {
    token: string;
    queryParams?: {
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: string;
        vectorQuery?: string;
        vectorDistance?: number;
        penaltyAlpha?: number;
        objectLimit?: number;
        name?: string;
        url?: string;
        title?: string;
        description?: string;
        author?: string;
        companyRagFolderId?: string;
        type?: string;
        language?: string;
        categoryId?: string;
        tagId?: string;
        rightsOwner?: string;
        creditLine?: string;
        usageTypeId?: string;
        rightsTermId?: string;
        territoryId?: string;
        allowedMediaId?: string;
        createdAtFrom?: string;
        createdAtTo?: string;
        updatedAtFrom?: string;
        updatedAtTo?: string;
        dateFrom?: string;
        dateTo?: string;
    };
}): Promise<GetCompanyFilesResponse> => {
    const params = new URLSearchParams();

    if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
                params.append(key, String(value));
            }
        });
    }

    const url = `/get-company-files${params.toString() ? `?${params.toString()}` : ""}`;

    return fetchAPI<GetCompanyFilesResponse>(url, "GET", undefined, token);
};
